/**
 * ui_theme.ts — ONE look for every panel, window, HUD chip and menu, and three themes to wear it in (2026-09-24).
 *
 * THE TOKEN LAW. Every piece of game UI reads its colours, type and window material from the `--ax-*` custom
 * properties on the document root — never a literal. The Esc options menu is the reference the tokens were cut
 * from (burnt-umber ink, a brass seam, the stencil wordmark face, Sora for reading, Plex Mono for numbers); the
 * other two themes keep the SAME structure (tabs, rows, segmented controls, the seam) and swap the materials.
 * `tools/ui/theme_codemod.py` migrated the older navy literals by ROLE (the CSS property + lightness + hue decide
 * the token), and `verify:ui-theme` fails on a navy literal that creeps back into a style string.
 *
 *   frontier  (default) umber ink · brass · rust · bone — the stencil title, a brass welded seam, paper grain
 *   borean    cold glacier slate · ice · aurora rose — Chakra Petch titles, frosted glass with an ice rim on top
 *   aurelian  lacquered aubergine · gold leaf · verdigris — Cinzel titles, a double gilt rule around every window
 *
 * Canvas drawing cannot read a CSS variable: a canvas UI asks `themeColor('--ax-text')` (the computed value,
 * cached per theme) and redraws in `onThemeChange` (the `atlas-ui-theme` event).
 *
 * MIRROR: index.html carries all three themes' tokens (Frontier on `:root`, the others on `[data-ax-theme]`, which its
 * head script sets from the saved choice) so the static page paints in the right colours before this module runs;
 * `verify:ui-theme` pins each block equal to its THEMES entry (2026-09-25: only Frontier was mirrored).
 */

export type ThemeId = 'frontier' | 'borean' | 'aurelian';

export interface ThemeDef {
  id: ThemeId;
  name: string;
  /** One line for the options menu: what the theme looks like. */
  blurb: string;
  /** Three swatches for the picker (ground, text, accent). */
  swatch: [string, string, string];
  tokens: Record<ThemeToken, string>;
}

/** Every token a theme must define (the harness checks all three themes carry all of them). */
export const THEME_TOKENS = [
  // surfaces, lines, text
  '--ax-bg', '--ax-bg2', '--ax-bg3', '--ax-sel', '--ax-line', '--ax-line2',
  '--ax-text', '--ax-soft', '--ax-muted', '--ax-hi',
  // accent + meaning
  '--ax-accent', '--ax-on-accent', '--ax-alert', '--ax-info', '--ax-good', '--ax-bad', '--ax-warn', '--ax-rare',
  // type
  '--ax-font', '--ax-display', '--ax-mono',
  // shape + window material
  '--ax-radius', '--ax-win-radius', '--ax-win-bg', '--ax-win-border', '--ax-win-frame', '--ax-win-blur',
  '--ax-win-outline', '--ax-seam-left', '--ax-seam-top', '--ax-bar-bg', '--ax-grain', '--ax-scrim', '--ax-shadow',
  // IDENTITY, not theme: the two civilisations' colours are the SAME in every theme (the Aurelian gold, the Borean ice
  // — the menu's tagline, the traveler's allegiance). verify:ui-theme pins them equal across the three.
  '--ax-civ-a', '--ax-civ-b',
] as const;
export type ThemeToken = (typeof THEME_TOKENS)[number];

const GRAIN = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 .92  0 0 0 0 .8  0 0 0 .07 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`;
const MONO = `'IBM Plex Mono',ui-monospace,monospace`;
/** The civilisations' own colours (identity): Aurelian gold, Borean ice. */
const CIV_A = '#e6b95a', CIV_B = '#6db3f2';

export const THEMES: Record<ThemeId, ThemeDef> = {
  frontier: {
    id: 'frontier', name: 'Frontier',
    blurb: 'Burnt-umber ink, brass and rust — the stencil title and a welded brass seam.',
    swatch: ['#120d0a', '#efe6d2', '#d9a441'],
    tokens: {
      '--ax-bg': '#120d0a', '--ax-bg2': '#1b1510', '--ax-bg3': '#2a2019', '--ax-sel': '#3a2b18',
      '--ax-line': '#3a2e24', '--ax-line2': '#4a3b2e',
      '--ax-text': '#efe6d2', '--ax-soft': '#d3c8b1', '--ax-muted': '#937f6b', '--ax-hi': '#d9a441', // muted: the menu's dust #8c7a66 lifted to 4.5:1
      '--ax-accent': '#d9a441', '--ax-on-accent': '#1a1209', '--ax-alert': '#c9573a', '--ax-info': '#5fa8c9',
      '--ax-good': '#9cc48a', '--ax-bad': '#e3735a', '--ax-warn': '#e0a044', '--ax-rare': '#b892d6',
      '--ax-font': `Sora,'Segoe UI',system-ui,sans-serif`,
      '--ax-display': `'Big Shoulders Stencil Display','Arial Narrow',Impact,sans-serif`,
      '--ax-mono': MONO,
      '--ax-radius': '3px', '--ax-win-radius': '3px',
      '--ax-win-bg': 'color-mix(in srgb, var(--ax-bg) 97%, transparent)',
      '--ax-win-border': '1px solid var(--ax-line)',
      '--ax-win-frame': '0 18px 50px rgba(0,0,0,.62)',
      '--ax-win-blur': 'none',
      '--ax-win-outline': '0 solid transparent',
      '--ax-seam-left': '2px solid var(--ax-accent)', '--ax-seam-top': '0 solid transparent',
      '--ax-bar-bg': 'transparent',
      '--ax-grain': GRAIN,
      '--ax-scrim': 'rgba(9,6,4,.64)',
      '--ax-shadow': 'rgba(0,0,0,.55)',
      '--ax-civ-a': CIV_A, '--ax-civ-b': CIV_B,
    },
  },
  borean: {
    id: 'borean', name: 'Borean',
    blurb: 'Cold glacier slate and ice — frosted glass with an ice rim along the top.',
    swatch: ['#13222a', '#eaf3f4', '#8fd3de'],
    tokens: {
      '--ax-bg': '#13222a', '--ax-bg2': '#1a2e38', '--ax-bg3': '#234049', '--ax-sel': '#1f4a57',
      '--ax-line': '#2f515c', '--ax-line2': '#3f6b77',
      '--ax-text': '#eaf3f4', '--ax-soft': '#bcd3d7', '--ax-muted': '#7d9ca3', '--ax-hi': '#9fdce6',
      '--ax-accent': '#8fd3de', '--ax-on-accent': '#082026', '--ax-alert': '#f08aa6', '--ax-info': '#b0b8ff',
      '--ax-good': '#7ed9a6', '--ax-bad': '#ff8a8a', '--ax-warn': '#f5c46a', '--ax-rare': '#c7a8ff',
      '--ax-font': `'IBM Plex Sans','Segoe UI',system-ui,sans-serif`,
      '--ax-display': `'Chakra Petch','Arial Narrow',sans-serif`,
      '--ax-mono': MONO,
      '--ax-radius': '6px', '--ax-win-radius': '12px',
      '--ax-win-bg': 'color-mix(in srgb, var(--ax-bg) 80%, transparent)',
      '--ax-win-border': '1px solid color-mix(in srgb, var(--ax-line2) 80%, transparent)',
      '--ax-win-frame': '0 18px 48px rgba(0,8,14,.55), inset 0 1px 0 rgba(255,255,255,.12)',
      '--ax-win-blur': 'blur(14px) saturate(1.25)',
      '--ax-win-outline': '0 solid transparent',
      '--ax-seam-left': '1px solid color-mix(in srgb, var(--ax-line2) 80%, transparent)', '--ax-seam-top': '2px solid var(--ax-accent)',
      '--ax-bar-bg': 'color-mix(in srgb, var(--ax-accent) 7%, transparent)',
      '--ax-grain': 'none',
      '--ax-scrim': 'rgba(4,12,16,.55)',
      '--ax-shadow': 'rgba(0,10,16,.5)',
      '--ax-civ-a': CIV_A, '--ax-civ-b': CIV_B,
    },
  },
  aurelian: {
    id: 'aurelian', name: 'Aurelian',
    blurb: 'Lacquered aubergine and gold leaf — inscription titles and a double gilt frame.',
    swatch: ['#170d12', '#f4e9da', '#e6be5c'],
    tokens: {
      '--ax-bg': '#170d12', '--ax-bg2': '#22141b', '--ax-bg3': '#301c26', '--ax-sel': '#3d2330',
      '--ax-line': '#4a2e3a', '--ax-line2': '#6b4453',
      '--ax-text': '#f4e9da', '--ax-soft': '#dcc9b6', '--ax-muted': '#a88c98', '--ax-hi': '#ebc464',
      '--ax-accent': '#e6be5c', '--ax-on-accent': '#22120a', '--ax-alert': '#ff7663', '--ax-info': '#7fcab7',
      '--ax-good': '#a3d18f', '--ax-bad': '#ff7f73', '--ax-warn': '#f2a650', '--ax-rare': '#d3a6ff',
      '--ax-font': `'Alegreya Sans','Segoe UI',system-ui,sans-serif`,
      '--ax-display': `'Cinzel',Georgia,serif`,
      '--ax-mono': MONO,
      '--ax-radius': '4px', '--ax-win-radius': '6px',
      '--ax-win-bg': 'color-mix(in srgb, var(--ax-bg) 97%, transparent)',
      '--ax-win-border': '1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent)',
      '--ax-win-frame': '0 18px 50px rgba(0,0,0,.6)',
      '--ax-win-blur': 'none',
      '--ax-win-outline': '1px solid color-mix(in srgb, var(--ax-accent) 32%, transparent)',
      '--ax-seam-left': '1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent)', '--ax-seam-top': '0 solid transparent',
      '--ax-bar-bg': 'color-mix(in srgb, var(--ax-accent) 6%, transparent)',
      '--ax-grain': 'none',
      '--ax-scrim': 'rgba(12,4,8,.62)',
      '--ax-shadow': 'rgba(0,0,0,.55)',
      '--ax-civ-a': CIV_A, '--ax-civ-b': CIV_B,
    },
  },
};

export const THEME_ORDER: readonly ThemeId[] = ['frontier', 'borean', 'aurelian'];
export const DEFAULT_THEME: ThemeId = 'frontier';
const STORE_KEY = 'atlas:ui-theme';

export function isThemeId(v: unknown): v is ThemeId {
  return v === 'frontier' || v === 'borean' || v === 'aurelian';
}

function block(sel: string, t: Record<string, string>): string {
  return `${sel}{${Object.entries(t).map(([k, v]) => `${k}:${v}`).join(';')}}`;
}

/**
 * The shared primitives every module inherits: the window chrome (lib/ui_window marks its parts), themed bare
 * controls inside game panels (a `<button>` or `<input>` a module left unstyled), scrollbars, the focus ring,
 * the text selection, and the classes new UI should use (`ax-btn`, `ax-input`, `ax-seg`, `ax-chip`, `ax-head`).
 */
const BASE_CSS = `
[data-ax-window]{background-color:var(--ax-win-bg);background-image:var(--ax-grain);border:var(--ax-win-border);border-left:var(--ax-seam-left);
  border-radius:var(--ax-win-radius);box-shadow:var(--ax-win-frame);-webkit-backdrop-filter:var(--ax-win-blur);backdrop-filter:var(--ax-win-blur);
  outline:var(--ax-win-outline);outline-offset:-4px;color:var(--ax-text);font:13px/1.45 var(--ax-font)}
[data-ax-window] [data-ax-bar]{background:var(--ax-bar-bg);border-bottom:1px solid var(--ax-line);border-top:var(--ax-seam-top)}
[data-ax-window] [data-ax-title]{font:600 16px/1.1 var(--ax-display);letter-spacing:.02em;color:var(--ax-hi)}
[data-ax-window] [data-ax-icon]{color:var(--ax-muted)}
[data-ax-window] [data-ax-chrome]{color:var(--ax-muted);border-radius:var(--ax-radius)}
[data-ax-window] [data-ax-chrome]:hover{background:var(--ax-bg3);color:var(--ax-text)}
[data-ax-window] [data-ax-grip]{color:var(--ax-line2)}
[data-ax-window] button:not([style]):not([class]),.ax-btn{font:500 13px/1.2 var(--ax-font);color:var(--ax-text);background:var(--ax-bg2);
  border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:5px 12px;cursor:pointer}
[data-ax-window] button:not([style]):not([class]):hover:not(:disabled),.ax-btn:hover:not(:disabled){background:var(--ax-bg3);border-color:var(--ax-muted)}
[data-ax-window] button:not([style]):not([class]):disabled,.ax-btn:disabled{opacity:.45;cursor:default}
.ax-btn.is-primary{background:var(--ax-accent);border-color:var(--ax-accent);color:var(--ax-on-accent);font-weight:600}
.ax-btn.is-primary:hover:not(:disabled){background:color-mix(in srgb, var(--ax-accent) 86%, white)}
[data-ax-window] input:not([type=range]):not([type=checkbox]):not([type=radio]):not([style]),[data-ax-window] select:not([style]),
[data-ax-window] textarea:not([style]),.ax-input{font:13px/1.3 var(--ax-font);color:var(--ax-text);background:var(--ax-bg2);
  border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:5px 8px}
[data-ax-window] input[type=range],[data-ax-window] input[type=checkbox],[data-ax-window] input[type=radio]{accent-color:var(--ax-accent)}
.ax-seg{display:inline-flex;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);overflow:hidden;background:var(--ax-bg2)}
.ax-seg button{background:none;border:0;border-right:1px solid var(--ax-line2);padding:4px 10px;font:500 12.5px/1.2 var(--ax-font);color:var(--ax-soft);cursor:pointer}
.ax-seg button:last-child{border-right:0}
.ax-seg button.on{background:var(--ax-accent);color:var(--ax-on-accent);font-weight:600}
.ax-chip{display:inline-flex;align-items:center;gap:6px;padding:2px 9px;border:1px solid var(--ax-line2);border-radius:999px;background:var(--ax-bg2);color:var(--ax-soft);font:500 12px/1.4 var(--ax-font)}
.ax-head{font:600 13px/1.3 var(--ax-font);color:var(--ax-hi);border-bottom:1px solid var(--ax-line);padding-bottom:5px}
.ax-num{font-family:var(--ax-mono);font-variant-numeric:tabular-nums}
[data-ax-window] *,#passport *,#forge *,#worldlogic *{scrollbar-width:thin;scrollbar-color:var(--ax-line2) transparent}
[data-ax-window] :focus-visible,.ax-btn:focus-visible,.ax-input:focus-visible{outline:2px solid var(--ax-info);outline-offset:2px}
/* THE PAD RING (2026-09-25): lib/ui_nav marks the pad's focus with data-ax-focus — its inline outline left style=""
   behind and pushed every bare control the pad had visited out of :not([style]) for good. !important as the inline
   style was: a module's outline:0 must never hide what A would press. */
[data-ax-focus]{outline:2px solid var(--ax-accent)!important;outline-offset:2px!important}
::selection{background:color-mix(in srgb, var(--ax-accent) 45%, transparent);color:var(--ax-text)}
@media (prefers-reduced-transparency:reduce){[data-ax-window]{-webkit-backdrop-filter:none;backdrop-filter:none;background-color:var(--ax-bg)}}
`;

/** The whole theme sheet: the frontier tokens on :root, the other two on their attribute, then the primitives. */
export function themeCss(): string {
  const out = [block(':root', THEMES.frontier.tokens)];
  for (const id of THEME_ORDER) if (id !== 'frontier') out.push(block(`:root[data-ax-theme="${id}"]`, THEMES[id].tokens));
  out.push(BASE_CSS);
  return out.join('\n');
}

let current: ThemeId = DEFAULT_THEME;
const colorCache = new Map<string, string>();

function readSaved(): ThemeId {
  try { const v = localStorage.getItem(STORE_KEY); return isThemeId(v) ? v : DEFAULT_THEME; } catch { return DEFAULT_THEME; }
}

/** Inject the sheet once and wear the saved theme. Call first thing at boot (main.ts). */
export function installTheme(): void {
  if (typeof document === 'undefined') return;
  if (!document.getElementById('ax-theme')) {
    const st = document.createElement('style');
    st.id = 'ax-theme';
    st.textContent = themeCss();
    document.head.prepend(st); // FIRST: module sheets injected later win their own specificity fights
  }
  applyTheme(readSaved(), false);
  // Probe lever: the worn theme, a setter, the list (the options menu's picker is the player's way in).
  (window as unknown as { __uiTheme?: unknown }).__uiTheme = { current: currentTheme, set: setTheme, list: () => [...THEME_ORDER] };
}

function applyTheme(id: ThemeId, persist: boolean): void {
  current = id;
  colorCache.clear();
  if (typeof document !== 'undefined') {
    if (id === DEFAULT_THEME) delete document.documentElement.dataset.axTheme;
    else document.documentElement.dataset.axTheme = id;
    if (persist) { try { localStorage.setItem(STORE_KEY, id); } catch { /* private mode */ } }
    window.dispatchEvent(new CustomEvent('atlas-ui-theme', { detail: id }));
  }
}

/** Wear a theme now (live — a look is judged by eye, like a sound level) and remember it. */
export function setTheme(id: ThemeId): void {
  if (!isThemeId(id)) return;
  applyTheme(id, true);
}

export function currentTheme(): ThemeId { return current; }

/**
 * Run `fn` after every theme change — a CANVAS UI redraws here (its colours were read through `themeColor`, so a
 * canvas painted before the change kept the old theme until its own next redraw; nothing listened, gaps wave
 * 2026-09-28). The cache is already cleared when `fn` runs. Returns the unsubscribe.
 */
export function onThemeChange(fn: (id: ThemeId) => void): () => void {
  if (typeof window === 'undefined') return () => { /* no DOM */ };
  const h = (e: Event): void => {
    const id = (e as CustomEvent<unknown>).detail;
    try { fn(isThemeId(id) ? id : current); } catch { /* one canvas's failure must not stop the others */ }
  };
  window.addEventListener('atlas-ui-theme', h);
  return () => window.removeEventListener('atlas-ui-theme', h);
}

/** A token's computed colour for canvas drawing (cached per theme; a literal fallback before the sheet exists). */
export function themeColor(token: ThemeToken, fallback?: string): string {
  const hit = colorCache.get(token);
  if (hit) return hit;
  let v = '';
  try { v = getComputedStyle(document.documentElement).getPropertyValue(token).trim(); } catch { /* no DOM */ }
  if (!v) v = fallback ?? THEMES[current].tokens[token];
  colorCache.set(token, v);
  return v;
}
